import { Tooltip } from "antd";
import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { InputNumberFieldV2 as InputNumberField } from "../../../../atoms/FormElement/InputNumberField";
import { formatCurrency } from "../../../../libs/utils";
import styles from "./ReceiptMultiInvoiceAllocation.module.scss";

type AllocationFormValues = {
  allocation: number;
};

type AllocationInputProps = {
  value: number;
  maxBalance: number;
  disabled?: boolean;
  onChange: (value: number) => void;
};

export const AllocationInput: React.FC<AllocationInputProps> = ({ value, maxBalance, disabled, onChange }) => {
  const skipExternalSync = useRef(false);

  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<AllocationFormValues>({
    defaultValues: { allocation: value },
    mode: "onChange",
  });

  const errorMessage = errors.allocation?.message as string | undefined;

  useEffect(() => {
    if (skipExternalSync.current) {
      skipExternalSync.current = false;
      return;
    }

    setValue("allocation", value, { shouldValidate: false });
  }, [value, setValue]);

  const handleAllocationChange = (nextValue: number | string | null) => {
    if (nextValue === null || nextValue === "") {
      void trigger("allocation");
      skipExternalSync.current = true;
      onChange(0);
      return;
    }
  
    const amount = Number(nextValue);
    if (Number.isNaN(amount)) return;
  
    // Never let a row's allocation exceed its balance.
    const clamped = Math.min(Math.max(amount, 0), maxBalance);
  
    void trigger("allocation");
  
    skipExternalSync.current = true;
    onChange(clamped);
  
    // If we clamped a too-large input, reflect the capped value back into the field.
    if (clamped !== amount) {
      setValue("allocation", clamped, { shouldValidate: true });
    }
  };

  return (
    <div
      className={styles.allocationCell}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Tooltip
        title={errorMessage}
        placement="topRight"
        open={Boolean(errorMessage)}
        getPopupContainer={() => document.body}
      >
        <span className={styles.allocationTooltipTarget}>
          <InputNumberField
            fieldname="allocation"
            control={control}
            format="currency"
            disabled={disabled}
            onChange={handleAllocationChange}
            rules={{
              validate: (fieldValue) => {
                if (fieldValue == null) return true;
                const amount = Number(fieldValue);
                if (Number.isNaN(amount)) return true;
                if (amount > maxBalance) {
                  return `Allocation cannot exceed balance of ${formatCurrency(maxBalance)}`;
                }
                return true;
              },
            }}
            formItemProps={{ className: styles.allocationFormItem }}
            inputNumberProps={{
              min: 0,
              // max: maxBalance,
              className: styles.allocationInput,
            }}
          />
        </span>
      </Tooltip>
    </div>
  );
};